import React, { useMemo } from 'react';
import { Link } from './Link';
import { JC_COOKIE_CONSENT_OPEN_EVENT } from '../utils/cookie-consent/constants';
import { usePageContext } from 'vike-react/usePageContext';
import type { FooterStaticStrings } from '../utils/footer/buildFooterStaticStrings';
import { buildFooterStaticStrings } from '../utils/footer/buildFooterStaticStrings';
import { useFooterStrings } from '../contexts/FooterStringsContext';
import { loadLocaleData } from '../utils/routing/url-helpers';
import { resolvePageLocale } from '../utils/routing/resolve-page-locale';
import { FooterSocialMediaBar } from './FooterSocialMediaBar';
import {
  buildEmployerScoreFooterLink,
  buildJobSearchGuideFooterLinks,
  buildJuniorsFooterLink,
} from '../utils/seo/indexation-discovery-links';
import { getLandingCopy } from '../../web-static/pages/landing/copy';
import { LandingFooter } from '../../web-static/pages/landing/components/LandingFooter';
import type { LandingLocale } from '../../web-static/pages/landing/types';
/** Curated blog/article links per locale - labels are locale-specific, not from message bundle. */
const BLOG_LINKS: Record<string, { href: string; label: string }[]> = {
  en: [
    { href: '/resources/jobs-in-finland/best-job-websites-finland', label: 'Best Job Websites in Finland' },
    { href: '/resources/jobs-in-finland/cool-finnish-tech-companies-english', label: 'Cool Finnish Tech Companies Hiring in English' },
    { href: '/resources/jobs-in-finland/highest-salaries-finland', label: 'Highest Salaries in Finland' },
    { href: '/resources/helsinki-job-market-2026', label: 'Job Market in Helsinki (2026)' },
    { href: '/resources/tampere-job-market-2026', label: 'Job Market in Tampere (2026)' },
  ],
  fi: [{ href: '/tyoelama/tyot-suomessa/parhaat-tyopaikkasivustot-suomessa', label: 'Parhaat työnhakusivustot Suomessa' }],
  sv: [{ href: '/arbetsliv/jobb-i-finland/basta-jobbsajter-finland', label: 'Bästa jobbsajter i Finland' }],
};

function FooterContent({
  locale,
  strings,
  bleedMargins = false,
}: {
  locale: string;
  strings: FooterStaticStrings;
  /** When true, apply negative horizontal margins for edge-to-edge chrome inside padded columns. Avoid when any ancestor uses overflow-x-hidden (e.g. SearchWrapper, SRP scroll) or the footer will clip. Default false. */
  bleedMargins?: boolean;
}) {
  const normalizedLocale = locale.toLowerCase().split('-')[0];
  const discoveryLocale =
    normalizedLocale === 'fi' || normalizedLocale === 'sv' ? normalizedLocale : 'en';
  const blogLinks = [
    ...buildJobSearchGuideFooterLinks(discoveryLocale),
    ...(BLOG_LINKS[normalizedLocale] ?? []),
    buildJuniorsFooterLink(discoveryLocale),
    buildEmployerScoreFooterLink(discoveryLocale),
  ];

  const widthClass = bleedMargins ? '-mx-2 md:-mx-4 lg:-mx-8' : 'w-full';

  return (
    <footer
        className={`bg-white dark:bg-[#1F1509] border-t border-[#DDD5C8] dark:border-[#4A3520] ${widthClass}`}
        data-footer="true"
        itemScope
        itemType="https://schema.org/WPFooter"
      >
        <div className="px-4 py-1.5 sm:px-6 lg:px-8 md:py-1.5 border-b border-[#DDD5C8] dark:border-[#4A3520]">
          <p className="text-[10px] md:text-xs text-[#7A6E5F] dark:text-[#B8A48C] text-center italic">
            {strings.aiTranslationDisclaimer}
          </p>
        </div>
        <div className="px-2 py-1.5 flex flex-col md:flex-row md:items-center md:justify-between md:px-4 md:py-2 gap-y-1.5 gap-x-4">
          <span className="text-xs text-[#7A6E5F] dark:text-[#B8A48C] text-center md:text-left">
            {strings.copyrightBefore}
            <a href="https://www.ashtonspina.com/" className="hover:underline text-[#3B5E45] dark:text-[#98CFA8] dark:hover:text-[#B6D4BC]">
              TechTactics Oy
            </a>
            {strings.copyrightAfter}
          </span>
          <ul className="flex flex-row flex-wrap items-center justify-center text-xs gap-x-2 md:gap-x-4">
            <li>
              <Link href="/resources" locale={locale} className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.resources}
              </Link>
            </li>
            <li>
              <Link href="/about" locale={locale} className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.aboutJobcrawls}
              </Link>
            </li>
            <li>
              <Link href="/partnerships" locale={locale} className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.partnerships}
              </Link>
            </li>
            <li>
              <Link href="/editorial-standards" locale={locale} className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.editorialStandards}
              </Link>
            </li>
            <li>
              <Link href="/contact" locale={locale} className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.contact}
              </Link>
            </li>
            <li>
              <Link href="/privacy" locale={locale} className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.privacy}
              </Link>
            </li>
            <li>
              <button
                type="button"
                onClick={() => {
                  if (typeof document !== 'undefined') {
                    document.dispatchEvent(new CustomEvent(JC_COOKIE_CONSENT_OPEN_EVENT));
                  }
                }}
                className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC] bg-transparent border-none cursor-pointer p-0 text-xs"
              >
                {strings.cookieSettings}
              </button>
            </li>
            <li>
              {/* Terms is a single English-only page for all locales — link directly to its
                  canonical URL instead of through locale-mapping, which produces the retired
                  /fi/kayttoehdot and /sv/villkor slugs (now redirect-only stubs). */}
              <Link href="/en/terms" locale={locale} className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.terms}
              </Link>
            </li>
            <li>
              {/* English-only developer/AI-integration docs — unprefixed URL by design (see web-static/pages/connector). */}
              <a href="/connector" className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.forDevelopers}
              </a>
            </li>
            <li>
              <Link href="/sitemap" locale={locale} className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.sitemap}
              </Link>
            </li>
          </ul>
        </div>
        <div className="px-4 pt-0 pb-2 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="w-full max-w-6xl">
              {blogLinks.length > 0 && (
                <>
                  <h3 className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] text-center mb-3">
                    {strings.fromTheBlog}
                  </h3>
                  <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
                    {blogLinks.map((item) => (
                      <li key={item.href}>
                        <Link href={item.href} locale={locale} className="text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline">
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="px-4 pb-2 sm:px-6 lg:px-8 md:pb-3">
          <ul className="flex flex-row flex-wrap items-center justify-center text-xs gap-x-2 md:gap-x-4 mb-2">
            <li>
              <Link href="/" locale="en" className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.jobcrawlsEn}
              </Link>
            </li>
            <li>
              <Link href="/" locale="fi" className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.jobcrawlsFi}
              </Link>
            </li>
            <li>
              <Link href="/" locale="sv" className="text-[#3B5E45] dark:text-[#98CFA8] hover:underline hover:text-[#2A4533] dark:hover:text-[#B6D4BC]">
                {strings.jobcrawlsSv}
              </Link>
            </li>
          </ul>
        </div>
        <div className="px-4 py-4 sm:px-6 lg:px-8 md:py-6">
          <div className="flex justify-center mb-6">
            <Link
              href="/locations"
              locale={locale}
              className="inline-flex items-center text-sm font-semibold text-[#3B5E45] hover:text-[#2A4533] dark:text-[#98CFA8] dark:hover:text-[#B6D4BC]"
            >
              {strings.locationsHub}
            </Link>
          </div>
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 w-full max-w-5xl mx-auto place-items-center">
              <div className="space-y-3 w-full max-w-xs">
                <h3 className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] text-center">
                  {strings.majorFinnishCities}
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link href="/jobs/location/Helsinki_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInHelsinki}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Tampere_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInTampere}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Espoo_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInEspoo}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Turku_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInTurku}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Oulu_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInOulu}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Vantaa_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInVantaa}
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="space-y-3 w-full max-w-xs">
                <h3 className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] text-center">
                  {strings.moreFinnishCities}
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link href="/jobs/location/Vaasa_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInVaasa}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Lahti_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInLahti}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Kuopio_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInKuopio}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Jyvaskyla_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInJyvaskyla}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Pori_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInPori}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Lappeenranta_Finland" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInLappeenranta}
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="space-y-3 w-full max-w-xs">
                <h3 className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] text-center">
                  {strings.remoteWorkAndCountries}
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link href="/jobs/location/Remote_-_Global" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.remoteGlobalJobs}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Remote_-_Europe" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.remoteEuropeJobs}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Remote_-_MENA" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.remoteMENAJobs}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs/location/Europe" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInEurope}
                    </Link>
                  </li>
                  <li>
                    {/* Root SRP link with keyword anchor ("Työpaikat Suomessa" etc.) — the SRP,
                        not the homepage, is the ranking target for open-jobs queries (P1-6). */}
                    <Link href="/jobs" locale={locale} className="text-sm text-[#3D3529] dark:text-[#C4A882] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] hover:underline block text-center">
                      {strings.jobsInFinland}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 pb-4 sm:px-6 lg:px-8 md:pb-6 border-t border-[#DDD5C8] dark:border-[#4A3520] pt-4">
          <FooterSocialMediaBar />
        </div>
      </footer>
  );
}

const MemoizedFooterContent = React.memo(FooterContent);

function isResourceTreePath(pathname: string): boolean {
  return /^(?:\/(en\/resources|fi\/tyoelama|sv\/arbetsliv|resources|tyoelama|arbetsliv))(?:\/|$)/.test(pathname);
}

function toLandingLocale(locale: string): LandingLocale {
  const normalized = locale.toLowerCase().split('-')[0];
  if (normalized === 'fi' || normalized === 'sv') {
    return normalized;
  }
  return 'en';
}

export interface FooterProps {
  /** Locale for links. When omitted, uses pageContext.locale. */
  locale?: string;
  /** Pre-computed footer strings (SSR). When omitted, uses FooterStringsContext. */
  strings?: FooterStaticStrings;
  /** When true, negative horizontal margins bleed the footer past padded parents. Default false so footers inside overflow-x-hidden shells are not clipped. */
  bleedMargins?: boolean;
}

function Footer({ locale: localeProp, strings: stringsProp, bleedMargins = false }: FooterProps = {}) {
  const pageContext = usePageContext();
  const stringsFromContext = useFooterStrings();
  const pathname =
    (pageContext as { urlPathname?: string; urlParsed?: { pathname?: string } })?.urlPathname ??
    (pageContext as { urlParsed?: { pathname?: string } })?.urlParsed?.pathname ??
    '';

  const locale = useMemo(() => {
    if (localeProp) return localeProp;
    const pc = pageContext as {
      locale?: string;
      urlPathname?: string;
      urlParsed?: { pathname?: string };
    };
    return resolvePageLocale({
      locale: pc?.locale,
      urlPathname: pc?.urlPathname ?? pc?.urlParsed?.pathname,
    });
  }, [localeProp, pageContext]);

  const fallbackStrings = useMemo(
    () => buildFooterStaticStrings(loadLocaleData(locale)),
    [locale]
  );

  const strings = stringsProp ?? stringsFromContext ?? fallbackStrings;

  if (isResourceTreePath(pathname)) {
    const landingLocale = toLandingLocale(locale);
    const landingCopy = getLandingCopy(landingLocale);

    return (
      <div className="lp-root">
        <LandingFooter
          footer={landingCopy.footer}
          locale={landingCopy.locale}
          jobsHref={landingCopy.jobsHref}
        />
      </div>
    );
  }

  return <MemoizedFooterContent locale={locale} strings={strings} bleedMargins={bleedMargins} />;
}

export default Footer;
